
import React, { useCallback } from "react";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { uploadTemporaryFile, isFileReference } from "@/utils/temporaryFileStorage";

interface DocumentFileUploadProps {
  form: UseFormReturn<any>;
  sectionIndex: number;
}

const DocumentFileUpload = ({ form, sectionIndex }: DocumentFileUploadProps) => {
  // Handle file selection and upload to temporary storage
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      console.log("File selected:", file);
      console.log("File name:", file.name);
      console.log("File type:", file.type);
      console.log("File size:", file.size);
      
      // Upload file to temporary storage
      const fileReference = await uploadTemporaryFile(file);
      if (fileReference) {
        console.log("File uploaded to temporary storage:", fileReference);
        // Store the file reference in the form
        form.setValue(`sections.${sectionIndex}.document_file`, fileReference);
      } else {
        console.error("Failed to upload file to temporary storage");
      }
    }
  }, [form, sectionIndex]);

  // Get current value
  const value = form.watch(`sections.${sectionIndex}.document_file`);

  // Display the file name if available
  const displayFileName = useCallback(() => {
    if (!value) return null;
    
    if (isFileReference(value)) {
      return value.name;
    } else if (value instanceof FileList && value.length > 0) {
      return value[0].name;
    }
    return null;
  }, [value]);
  
  const fileName = displayFileName();

  return (
    <FormField
      control={form.control}
      name={`sections.${sectionIndex}.document_file`}
      render={({ field: { onChange, value: _value, ...field } }) => (
        <FormItem>
          <FormLabel>Identity Document</FormLabel>
          <FormControl>
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
                id={`document-upload-${sectionIndex}`}
                {...field}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  document
                    .getElementById(`document-upload-${sectionIndex}`)
                    ?.click()
                }
                className="w-full bg-nation-teal hover:bg-nation-teal-dark text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Identity Document
              </Button>
              {fileName && (
                <span className="text-sm text-muted-foreground">
                  {fileName}
                </span>
              )}
            </div>
          </FormControl>
          <FormDescription>
            Please upload a clear scan or photo of your ID document.
            Accepted formats: PDF, JPEG, PNG. Max file size: 5MB.
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default DocumentFileUpload;
